import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../store/lang";

const COLORS = [
  { name: "gold", bg: "#f3e5ab", glow: "rgba(243,229,171,0.8)" },
  { name: "rose", bg: "#f4d3d5", glow: "rgba(244,211,213,0.8)" },
  { name: "lavender", bg: "#c9b6f2", glow: "rgba(201,182,242,0.8)" },
  { name: "emerald", bg: "#9adbcf", glow: "rgba(154,219,207,0.8)" },
];

export const LanternLauncher = () => {
  const { ur } = useLang();
  const [color, setColor] = useState(COLORS[0]);
  const [message, setMessage] = useState("");
  const [lanterns, setLanterns] = useState([]);

  const launch = () => {
    if (!message.trim()) {
      toast.error(ur ? "پہلے ایک چھوٹی سی دعا لکھیں" : "Write a tiny blessing first");
      return;
    }
    const id = Math.random().toString(36).slice(2);
    setLanterns((l) => [...l, { id, color, x: 15 + Math.random() * 70 }]);
    setTimeout(() => setLanterns((l) => l.filter((x) => x.id !== id)), 7200);
    toast.success(ur ? "آپ کا چراغ ستاروں کی طرف اڑ رہا ہے" : "Your lantern is floating up to the stars");
    setMessage("");
  };

  return (
    <div data-testid="lantern-launcher-game">
      <div className="relative h-[300px] rounded-2xl overflow-hidden bg-gradient-to-b from-[#080a18] to-[#19102e] border border-amber-200/15 mb-5">
        <div className="absolute inset-0 opacity-60">
          {Array.from({ length: 26 }).map((_, i) => (
            <span
              key={i}
              className="absolute w-1 h-1 rounded-full bg-amber-100/70"
              style={{ left: `${(i * 37) % 100}%`, top: `${(i * 23) % 90}%` }}
            />
          ))}
        </div>
        <AnimatePresence>
          {lanterns.map((l) => (
            <motion.div
              key={l.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lantern-rise absolute bottom-2"
              style={{ left: `${l.x}%` }}
            >
              <div
                className="w-9 h-12 rounded-t-full rounded-b-lg"
                style={{
                  background: `radial-gradient(circle at 50% 70%, ${l.color.bg}, ${l.color.bg}88 60%, transparent)`,
                  boxShadow: `0 0 26px 8px ${l.color.glow}`,
                }}
              />
            </motion.div>
          ))}
        </AnimatePresence>
        {lanterns.length === 0 && (
          <p className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm font-light px-8 text-center" dir={ur ? "rtl" : "ltr"}>
            {ur ? "آسمان آپ کے دعا بھری چراغ کا انتظار کر رہا ہے…" : "The sky is waiting for your blessing lantern…"}
          </p>
        )}
      </div>

      <div className="flex items-center justify-center gap-3 mb-4">
        {COLORS.map((c) => (
          <button
            key={c.name}
            data-testid={`lantern-color-${c.name}`}
            onClick={() => setColor(c)}
            aria-label={`${c.name} lantern`}
            className={`w-9 h-9 rounded-full transition-transform duration-300 ${color.name === c.name ? "scale-125 ring-2 ring-amber-200" : "opacity-60 hover:opacity-100"}`}
            style={{ background: c.bg, boxShadow: `0 0 14px ${c.glow}` }}
          />
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
        <input
          data-testid="lantern-input-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && launch()}
          maxLength={80}
          placeholder={ur ? "ہایا کے لیے دعا لکھیں…" : "Write a blessing for Haya…"}
          className="flex-1 bg-white/5 border border-amber-200/25 rounded-full px-5 py-3 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-amber-300/60 transition-colors duration-300"
          dir={ur ? "rtl" : "ltr"}
        />
        <button data-testid="lantern-launch-btn" onClick={launch} className="btn-gold justify-center">
          <Send size={14} /> {ur ? "آسمان پر بھیجیں" : "Release to the Sky"}
        </button>
      </div>
    </div>
  );
};
